import React, { useState, useEffect, useCallback } from 'react'
import { supabase } from '../../lib/supabase'

var SYNC_ICON = String.fromCodePoint(0x1F504)
var LINK_ICON = String.fromCodePoint(0x1F517)
var GCAL_ICON = String.fromCodePoint(0x1F4C5)

export default function GoogleCalendarSync({ userId }) {
  var [connected, setConnected] = useState(false)
  var [lastSync, setLastSync] = useState(null)
  var [syncing, setSyncing] = useState(false)
  var [message, setMessage] = useState('')
  var [loading, setLoading] = useState(true)

  var checkConnection = useCallback(async function() {
    if (!userId) return
    setLoading(true)
    var { data } = await supabase
      .from('google_tokens')
      .select('last_sync_at')
      .eq('user_id', userId)
      .single()
    setConnected(!!data)
    setLastSync(data?.last_sync_at || null)
    setLoading(false)
  }, [userId])

  useEffect(function() {
    checkConnection()
  }, [checkConnection])

  var handleConnect = function() {
    window.location.href = '/api/google-auth?user_id=' + userId
  }

  var handleDisconnect = async function() {
    if (!window.confirm('Disconnect Google Calendar? Your synced events will remain.')) return
    setSyncing(true)
    var resp = await fetch('/api/google-sync', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ user_id: userId, action: 'disconnect' })
    })
    var data = await resp.json()
    if (data.success) {
      setConnected(false)
      setLastSync(null)
      showMsg('Google Calendar disconnected')
    } else {
      showMsg('Error: ' + (data.error || 'Disconnect failed'))
    }
    setSyncing(false)
  }

  var handleSync = async function(action) {
    setSyncing(true)
    setMessage('')
    try {
      var resp = await fetch('/api/google-sync', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: userId, action: action })
      })
      var data = await resp.json()
      if (data.success) {
        showMsg(data.message || 'Sync complete!')
        setLastSync(new Date().toISOString())
        if (data.errors && data.errors.length > 0) {
          showMsg(data.message + ' (' + data.errors.length + ' errors)')
        }
      } else {
        showMsg('Error: ' + (data.error || 'Sync failed'))
      }
    } catch (err) {
      showMsg('Sync failed: ' + err.message)
    }
    setSyncing(false)
  }

  var showMsg = function(msg) {
    setMessage(msg)
    setTimeout(function() { setMessage('') }, 5000)
  }

  if (loading) return null

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <span style={styles.headerIcon}>{GCAL_ICON}</span>
        <span style={styles.headerTitle}>GOOGLE CALENDAR</span>
        <span style={{
          ...styles.statusDot,
          backgroundColor: connected ? '#4a8c6a' : 'rgba(255,255,255,0.15)'
        }} />
        <span style={{
          ...styles.statusText,
          color: connected ? '#4a8c6a' : 'rgba(255,255,255,0.3)'
        }}>
          {connected ? 'CONNECTED' : 'NOT CONNECTED'}
        </span>
      </div>

      {message && (
        <div style={{
          ...styles.message,
          borderColor: message.includes('Error') ? 'rgba(224,92,92,0.3)' : 'rgba(74,140,106,0.3)',
          color: message.includes('Error') ? '#e05c5c' : '#4a8c6a',
          backgroundColor: message.includes('Error') ? 'rgba(224,92,92,0.08)' : 'rgba(74,140,106,0.08)',
        }}>
          {message}
        </div>
      )}

      {!connected ? (
        <div style={styles.connectSection}>
          <div style={styles.connectDesc}>
            Connect your Google Calendar for two-way sync. Your jobs will appear in Google Calendar and your Google events will show in the app.
          </div>
          <button onClick={handleConnect} style={styles.connectBtn}>
            {LINK_ICON} CONNECT GOOGLE CALENDAR
          </button>
        </div>
      ) : (
        <div style={styles.syncSection}>
          {lastSync && (
            <div style={styles.lastSync}>
              Last synced: {new Date(lastSync).toLocaleString('en-US', {
                month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit'
              })}
            </div>
          )}
          <div style={styles.syncButtons}>
            <button
              onClick={function() { handleSync('sync') }}
              disabled={syncing}
              style={styles.syncBtn}
            >
              {syncing ? 'SYNCING...' : SYNC_ICON + ' SYNC NOW'}
            </button>
            <button
              onClick={function() { handleSync('push') }}
              disabled={syncing}
              style={styles.pushBtn}
            >
              PUSH JOBS {String.fromCodePoint(0x2192)} GOOGLE
            </button>
            <button
              onClick={function() { handleSync('pull') }}
              disabled={syncing}
              style={styles.pullBtn}
            >
              PULL GOOGLE {String.fromCodePoint(0x2192)} APP
            </button>
            <button
              onClick={handleDisconnect}
              disabled={syncing}
              style={styles.disconnectBtn}
            >
              DISCONNECT
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

var styles = {
  container: {
    padding: '16px 20px',
    backgroundColor: 'rgba(255,255,255,0.02)',
    border: '1px solid rgba(255,255,255,0.06)',
    borderRadius: '4px',
    marginBottom: '20px',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '12px',
  },
  headerIcon: { fontSize: '16px' },
  headerTitle: {
    fontFamily: "'Barlow Condensed', sans-serif",
    fontSize: '12px',
    fontWeight: 600,
    letterSpacing: '2px',
    color: 'rgba(255,255,255,0.5)',
  },
  statusDot: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    marginLeft: '8px',
  },
  statusText: {
    fontFamily: "'Barlow Condensed', sans-serif",
    fontSize: '10px',
    fontWeight: 600,
    letterSpacing: '1.5px',
  },
  message: {
    padding: '10px 14px',
    border: '1px solid',
    borderRadius: '3px',
    fontSize: '13px',
    marginBottom: '12px',
  },
  connectSection: {},
  connectDesc: {
    fontSize: '13px',
    color: 'rgba(255,255,255,0.35)',
    lineHeight: 1.5,
    marginBottom: '14px',
  },
  connectBtn: {
    padding: '12px 24px',
    backgroundColor: 'rgba(66,133,244,0.15)',
    border: '1px solid rgba(66,133,244,0.4)',
    borderRadius: '3px',
    color: '#4285F4',
    fontFamily: "'Barlow Condensed', sans-serif",
    fontSize: '13px',
    fontWeight: 700,
    letterSpacing: '2px',
    cursor: 'pointer',
    width: '100%',
  },
  syncSection: {},
  lastSync: {
    fontSize: '12px',
    color: 'rgba(255,255,255,0.3)',
    marginBottom: '12px',
  },
  syncButtons: {
    display: 'flex',
    gap: '8px',
    flexWrap: 'wrap',
  },
  syncBtn: {
    padding: '10px 20px',
    backgroundColor: '#b48c3c',
    border: 'none',
    borderRadius: '3px',
    color: '#0a0f1a',
    fontFamily: "'Barlow Condensed', sans-serif",
    fontSize: '12px',
    fontWeight: 700,
    letterSpacing: '2px',
    cursor: 'pointer',
  },
  pushBtn: {
    padding: '10px 16px',
    backgroundColor: 'rgba(74,140,106,0.15)',
    border: '1px solid rgba(74,140,106,0.4)',
    borderRadius: '3px',
    color: '#4a8c6a',
    fontFamily: "'Barlow Condensed', sans-serif",
    fontSize: '11px',
    fontWeight: 600,
    letterSpacing: '1.5px',
    cursor: 'pointer',
  },
  pullBtn: {
    padding: '10px 16px',
    backgroundColor: 'rgba(74,106,140,0.15)',
    border: '1px solid rgba(74,106,140,0.4)',
    borderRadius: '3px',
    color: '#4a6a8c',
    fontFamily: "'Barlow Condensed', sans-serif",
    fontSize: '11px',
    fontWeight: 600,
    letterSpacing: '1.5px',
    cursor: 'pointer',
  },
  disconnectBtn: {
    padding: '10px 16px',
    backgroundColor: 'transparent',
    border: '1px solid rgba(224,92,92,0.3)',
    borderRadius: '3px',
    color: '#e05c5c',
    fontFamily: "'Barlow Condensed', sans-serif",
    fontSize: '11px',
    fontWeight: 600,
    letterSpacing: '1.5px',
    cursor: 'pointer',
    marginLeft: 'auto',
  },
}
